/**
 * Investor-Portal Layout (#1138 Tag 3) — Sidebar + Topbar + Admin-Section.
 */
import { NavLink, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useInvestorAuth } from '../../context/InvestorAuthContext';

const NAV = [
  { to: '/investor', label: 'Home', end: true },
  { to: '/investor/guv', label: 'GuV' },
  { to: '/investor/aufwendungen', label: 'Aufwendungen' },
  { to: '/investor/bilanz', label: 'Bilanz' },
  { to: '/investor/cashflow', label: 'Cashflow' },
  { to: '/investor/liquiditaet', label: 'Liquiditätsplanung' },
  { to: '/investor/annahmen', label: 'Annahmen' },
  { to: '/investor/szenario', label: 'Szenario-Rechner' },
  { to: '/investor/audit', label: 'Buchhaltungs-Audit' },
  { to: '/investor/ebitda-audit', label: 'EBITDA-Audit' },
];

// #1172 + #1178 — Alle 5 Segmente live; #1180 — EBITDA-Wasserfall als Abschluss
const SEGMENTE = [
  { to: '/investor/segmente/outbound', label: 'Outbound' },
  { to: '/investor/segmente/inbound', label: 'Inbound & Return' },
  { to: '/investor/segmente/lager', label: 'Lager / Warehousing' },
  { to: '/investor/segmente/carrier', label: 'Porto & Transport' },
  { to: '/investor/segmente/material', label: 'Material' },
  { to: '/investor/segmente/ebitda', label: 'EBITDA-Übersicht' },
];

const ADMIN_NAV = [
  { to: '/investor/report', label: 'Investor-Report' },
  { to: '/investor/admin/mapping', label: 'Mapping-Editor' },
  { to: '/investor/admin/plan-editor', label: 'Plan-Editor' },
  { to: '/investor/admin/users', label: 'Benutzer' },
  { to: '/investor/admin/audit-log', label: 'Audit-Log' },
];

export default function InvestorLayout({ children }) {
  const { user, logout } = useInvestorAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [version, setVersion] = useState('');

  // #1839 B10: echte Version statt hartem Text — /api/health ist ohne Auth lesbar.
  useEffect(() => {
    fetch('/api/health')
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d?.version) setVersion(d.version); })
      .catch(() => {});
  }, []);

  async function handleLogout() {
    await logout();
    navigate('/investor/login', { replace: true });
  }

  return (
    <div className="min-h-screen bg-[#F8F9FC] flex">
      {/* Sidebar */}
      <aside className={`${mobileOpen ? 'block' : 'hidden'} md:block w-56 bg-white border-r border-[#E5E7EB] flex-shrink-0 flex flex-col`}>
        <div className="px-4 h-14 flex items-center gap-2 border-b border-[#E5E7EB]">
          <div className="w-8 h-8 rounded-lg bg-[#338edc] flex items-center justify-center text-white text-xs font-bold">W</div>
          <div className="text-sm font-semibold text-[#1A1A2E]">Investor</div>
        </div>
        <nav className="px-2 py-3 flex-1 overflow-y-auto">
          {NAV.slice(0, 3).map(n => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `block px-3 py-1.5 mb-0.5 text-sm rounded-md ${
                  isActive
                    ? 'text-[#338edc] font-medium border-l-[3px] border-[#338edc] pl-[9px]'
                    : 'text-[#4B5563] hover:bg-[#F8F9FC]'
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}

          {/* Segmente-Gruppe */}
          <div className="mt-3 px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF]">Segmente</div>
          {SEGMENTE.map((s, idx) => s.soon ? (
            <div
              key={`soon-${idx}`}
              className="flex items-center justify-between px-3 py-1.5 mb-0.5 text-sm text-[#9CA3AF] cursor-not-allowed select-none"
              title="Bald verfügbar"
            >
              <span>{s.label}</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F8F9FC] border border-[#E5E7EB] text-[#9CA3AF]">bald</span>
            </div>
          ) : (
            <NavLink
              key={s.to}
              to={s.to}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `block px-3 py-1.5 mb-0.5 text-sm rounded-md ${
                  isActive
                    ? 'text-[#338edc] font-medium border-l-[3px] border-[#338edc] pl-[9px]'
                    : 'text-[#4B5563] hover:bg-[#F8F9FC]'
                }`
              }
            >
              {s.label}
            </NavLink>
          ))}

          {/* Restliche Top-Level Items */}
          <div className="mt-3" />
          {NAV.slice(3).map(n => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `block px-3 py-1.5 mb-0.5 text-sm rounded-md ${
                  isActive
                    ? 'text-[#338edc] font-medium border-l-[3px] border-[#338edc] pl-[9px]'
                    : 'text-[#4B5563] hover:bg-[#F8F9FC]'
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}

          {user?.role === 'admin' && (
            <>
              <div className="mt-5 px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#9CA3AF]">Admin</div>
              {ADMIN_NAV.map(n => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `block px-3 py-1.5 mb-0.5 text-sm rounded-md ${
                      isActive
                        ? 'text-[#338edc] font-medium border-l-[3px] border-[#338edc] pl-[9px]'
                        : 'text-[#4B5563] hover:bg-[#F8F9FC]'
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </>
          )}
        </nav>
        <div className="px-4 py-3 border-t border-[#E5E7EB] text-[11px] text-[#9CA3AF]">
          {version ? `WeShip v${version}` : 'WeShip'}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-[#E5E7EB] h-14 flex items-center justify-between px-4 md:px-6 sticky top-0 z-10">
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-1.5 rounded-md hover:bg-[#F8F9FC]"
            aria-label="Menu"
          >
            <svg width="20" height="20" viewBox="0 0 20 20"><path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.5" /></svg>
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#6B7280] hidden sm:inline">
              {user ? `${user.vorname || ''} ${user.nachname || ''}`.trim() || user.email : ''}
            </span>
            {user?.role === 'admin' && (
              <span className="px-1.5 py-0.5 rounded bg-[#338edc]/10 text-[#338edc] text-[10px] font-medium">admin</span>
            )}
            <button
              onClick={handleLogout}
              className="text-xs text-[#4B5563] hover:text-[#1A1A2E] hover:underline"
            >
              Abmelden
            </button>
          </div>
        </header>
        <main className="flex-1 px-4 md:px-6 py-6 max-w-7xl w-full mx-auto">
          {user?.must_change_password && <ChangePasswordBanner />}
          {children}
        </main>
      </div>
    </div>
  );
}

function ChangePasswordBanner() {
  const [open, setOpen] = useState(false);
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const { refresh } = useInvestorAuth();
  const API = import.meta.env.VITE_API_URL || '';

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`${API}/api/investor/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ old_password: oldPw, new_password: newPw }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Fehler');
      await refresh();
      setOpen(false);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-900 text-xs px-3 py-2 rounded-lg flex items-center justify-between">
        <span>Du nutzt noch das Initial-Passwort — bitte ändern.</span>
        <button onClick={() => setOpen(true)} className="font-medium hover:underline">Jetzt ändern</button>
      </div>
    );
  }

  return (
    <div className="mb-4 bg-white border border-[#E5E7EB] rounded-lg p-4">
      <form onSubmit={submit} className="flex items-end gap-2 flex-wrap">
        <label className="block">
          <span className="text-[11px] text-[#6B7280]">Altes Passwort</span>
          <input type="password" required value={oldPw} onChange={e => setOldPw(e.target.value)}
            className="block mt-0.5 px-2 py-1 text-sm border border-[#E5E7EB] rounded-md" />
        </label>
        <label className="block">
          <span className="text-[11px] text-[#6B7280]">Neues Passwort (min. 10)</span>
          <input type="password" required minLength={10} value={newPw} onChange={e => setNewPw(e.target.value)}
            className="block mt-0.5 px-2 py-1 text-sm border border-[#E5E7EB] rounded-md" />
        </label>
        <button type="submit" disabled={busy} className="text-xs px-3 py-1.5 rounded-md bg-[#338edc] text-white font-medium disabled:opacity-60">
          {busy ? 'Speichere…' : 'Speichern'}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="text-xs px-2 py-1.5 text-[#6B7280] hover:text-[#1A1A2E]">Abbrechen</button>
        {err && <div className="text-xs text-red-600 w-full">{err}</div>}
      </form>
    </div>
  );
}
